import { useTables } from '@/hooks';
import { ENCRYPT } from '@/constants';
import pacote from '../../../package.json';
import { openDB as idb, deleteDB as idbDeleteDatabase, IDBPDatabase } from 'idb';
import { useEvents } from '../use-events/useEvents';
import { ProductProps, TableTypes, tableKeys } from '@/types';
import { unregisterServiceWorker } from '@/shared/serviceWorkFunctions';

export type Tables = (typeof tableKeys)[number];
const listTables: Tables[] = [...tableKeys];
const TABLE_ALIASES: Record<string, Tables> = {
  preco_novo: 'preco',
  saldo_flex_novo: 'saldo_flex',
  campanhaClientes: 'campanha_cliente',
};

// localStorage.clear();

// // Limpar SessionStorage
// sessionStorage.clear();

// // Limpar IndexedDB
// indexedDB.databases().then(dbs => {
//   dbs.forEach(db => {
//     indexedDB.deleteDatabase(db.name || '');
//   });
// });

export const toCoded = (str: string, forceEncrypt?: boolean, noReplace?: boolean): string => {
  if (!str || (!ENCRYPT && !forceEncrypt)) return str;

  try {
    const filteredStr = noReplace ? str : str.replace(/[^a-zA-Z]/g, '');
    const encoder = new TextEncoder();
    const uint8Array = encoder.encode(filteredStr);
    const binaryStr = String.fromCharCode(...uint8Array);
    return window.btoa(binaryStr).replace(/[^a-zA-Z]/g, '');
  } catch (error) {
    return noReplace ? str : str.replace(/[^a-zA-Z]/g, '');
  }
};

const DB_NAME = toCoded('BATE_FORTE');
const DB_IMAGES_NAME = toCoded('BATE_FORTE_IMAGES');
const DB_VERSION = Number(pacote.version.replace('.', ''));
const RETRY_DELAY = 300;
const MAX_RETRIES = 5;

/** Tempo extra após `deleteDatabase` resolver (mobile/PWA costuma precisar de um tick a mais). */
const IDB_DELETE_SETTLE_MS = 450;

const settleAfterIdbDelete = () => new Promise<void>((r) => setTimeout(r, IDB_DELETE_SETTLE_MS));

export const deleteDB = async (): Promise<void> => {
  try {
    await idbDeleteDatabase(DB_NAME, {
      blocked() {
        console.warn('[IDB] Exclusão do banco principal bloqueada até outras conexões fecharem');
      },
    });
    await settleAfterIdbDelete();
  } catch (error) {
    console.error('Erro ao deletar banco de dados:', error);
  }
};

export const deleteImagesDB = async (): Promise<void> => {
  try {
    await idbDeleteDatabase(DB_IMAGES_NAME, {
      blocked() {
        console.warn('[IDB] Exclusão do banco de imagens bloqueada até outras conexões fecharem');
      },
    });
    await settleAfterIdbDelete();
  } catch (error) {
    console.error('Erro ao deletar banco de imagens:', error);
  }
};

/**
 * Apaga os dois IndexedDB do app em sequência e só retorna quando o `idb` concluir (Promise real).
 * Usado no reset forçado por versão; retorna `false` se alguma exclusão falhar.
 */
export const deleteAppIndexedDatabasesCompletely = async (): Promise<boolean> => {
  let ok = true;
  try {
    await idbDeleteDatabase(DB_NAME, {
      blocked() {
        console.warn('[IDB] reset: banco principal bloqueado, aguardando fechamento de conexões');
      },
    });
    await settleAfterIdbDelete();
  } catch (e) {
    ok = false;
    console.error('[IDB] Falha ao excluir banco principal:', e);
  }
  try {
    await idbDeleteDatabase(DB_IMAGES_NAME, {
      blocked() {
        console.warn('[IDB] reset: banco de imagens bloqueado, aguardando fechamento de conexões');
      },
    });
    await settleAfterIdbDelete();
  } catch (e) {
    ok = false;
    console.error('[IDB] Falha ao excluir banco de imagens:', e);
  }
  return ok;
};

const createObjectStore = (db: IDBPDatabase, name: string): void => {
  if (name === 'imagens') return;

  const tableName = toCoded(name);
  if (!db.objectStoreNames.contains(tableName)) {
    db.createObjectStore(tableName, {
      keyPath: toCoded(name),
      autoIncrement: true,
    });
  }
};

const checkAndCreateMissingTables = async (): Promise<IDBPDatabase> => {
  let db = await idb(DB_NAME, DB_VERSION);
  const existingStores = Array.from(db.objectStoreNames);

  const missingTables = listTables.filter((tableName) => tableName !== 'imagens' && !existingStores.includes(toCoded(tableName)));

  if (missingTables.length > 0) {
    db.close();

    const newVersion = listTables.length + (Math.floor(Date.now() / 1000) % 1000);

    db = await idb(DB_NAME, newVersion, {
      upgrade(db) {
        listTables.forEach((name) => createObjectStore(db, name));
      },
    });
  }

  return db;
};

export const openDBConnection = async (): Promise<IDBPDatabase> => {
  try {
    let db = await idb(DB_NAME, DB_VERSION, {
      upgrade(db) {
        listTables.forEach((name) => createObjectStore(db, name));
      },
    });

    const existingStores = Array.from(db.objectStoreNames);
    const missingTables = listTables.filter(
      (tableName) => tableName !== 'imagens' && !existingStores.includes(toCoded(tableName))
    );

    if (missingTables.length > 0) {
      db.close();
      db = await checkAndCreateMissingTables();
    }

    return db;
  } catch (error) {
    console.error('Erro ao abrir conexão com banco de dados:', error);
    await deleteDB();
    await unregisterServiceWorker({});
    throw error;
  }
};

export const openImagesDBConnection = async (): Promise<IDBPDatabase> => {
  try {
    return await idb(DB_IMAGES_NAME, DB_VERSION, {
      upgrade(db) {
        const storeName = toCoded('imagens');
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName, {
            keyPath: storeName,
            autoIncrement: true,
          });
        }
      },
    });
  } catch (error) {
    console.error('Erro ao abrir conexão com banco de imagens:', error);
    throw error;
  }
};

const handleGet = async <T extends Tables>(name: T, currDB: IDBPDatabase): Promise<TableTypes[T]> => {
  try {
    const transaction = currDB.transaction(toCoded(name), 'readonly');
    const store = transaction.objectStore(toCoded(name));
    const allKeys = await store.getAllKeys();
    const lastKey = allKeys[allKeys.length - 1];
    if (lastKey == null) {
      return [] as TableTypes[T];
    }

    const lastData = await store.get(lastKey as IDBValidKey);
    const resp = (lastData?.data ?? []) as TableTypes[T];
    return name === 'restricaoProdutoCliente' ? resp || [] : resp;
  } catch (error) {
    console.info(`Erro ao obter dados da tabela ${name}:`, error);
    return [] as TableTypes[T];
  }
};

export const useDbFunctions = () => {
  const { tables, setOnTables, isLoading } = useTables();

  const normalizeTableName = (name: string): Tables | null => {
    const normalized = (TABLE_ALIASES[name] || name) as Tables;
    return listTables.includes(normalized) ? normalized : null;
  };

  const handleProductList = async <T extends Tables>(
    _: T,
    currDB: IDBPDatabase,
    COD_CLIENTE?: number
  ): Promise<TableTypes[T]> => {
    try {
      const [list, restrictedList] = await Promise.all([
        handleGet('produtos', currDB),
        handleGet('restricaoProdutoCliente', currDB),
      ]);

      const restrictions = new Set(
        restrictedList?.filter((r) => r?.COD_CLIENTE === COD_CLIENTE)?.map((r) => r?.COD_PRODUTO) || []
      );

      return list?.filter((p) => !restrictions.has(p.COD_PRODUTO)) as TableTypes[T];
    } catch (error) {
      console.error('Erro ao processar lista de produtos:', error);
      return [] as TableTypes[T];
    }
  };

  const closeDBConnection = (db: IDBPDatabase | undefined): void => {
    if (db) {
      db.close();
    }
  };

  const getImagesFromDB = async () => {
    try {
      const currDB = await openImagesDBConnection();
      const transaction = currDB.transaction(toCoded('imagens'), 'readonly');
      const store = transaction.objectStore(toCoded('imagens'));
      const allKeys = await store.getAllKeys();
      const lastKey = allKeys[allKeys.length - 1];
      if (lastKey == null) {
        closeDBConnection(currDB);
        return null;
      }
      const lastData = await store.get(lastKey as IDBValidKey);
      closeDBConnection(currDB);
      return lastData?.data ?? null;
    } catch (error) {
      console.error('Erro ao obter imagens do banco:', error);
      return null;
    }
  };

  const setImagesOnDB = async (data: any): Promise<void> => {
    if (!isValidData(data)) return;

    try {
      const currDB = await openImagesDBConnection().catch(err => {
        throw new Error(`Erro ao abrir conexão com banco de dados para salvar imagens: ${err?.message ?? err}`);
      });
      const transaction = currDB.transaction(toCoded('imagens'), 'readwrite');
      const store = transaction.objectStore(toCoded('imagens'));

      const allKeys = await store.getAllKeys();
      if (allKeys.length) {
        await store.delete(allKeys[allKeys.length - 1]).catch(err => {
          throw new Error(`Erro ao deletar imagens do banco de dados para salvar dados em imagens: ${err?.message ?? err}`);
        });
      }

      await store.put({ data, keyPath: toCoded('imagens') }).catch(err => {
        throw new Error(`Erro ao salvar imagens no banco de dados para salvar dados em imagens: ${err?.message ?? err}`);
      });
      
      await transaction.done.catch(err => {
        throw new Error(`Erro ao fechar transação do banco de dados para salvar dados em imagens: ${err?.message ?? err}`);
      });
      
      closeDBConnection(currDB);
    } catch (error) {
      console.error('Erro ao salvar imagens no banco:', error);
      return Promise.reject(error);
    }
  };

  const isValidData = (data: any): boolean => {
    return !!(
      data &&
      typeof data === 'object' &&
      !(Array.isArray(data) && data.length === 0) &&
      !(typeof data === 'object' && Object.keys(data).length === 0)
    );
  };

  const getOnDB = async <T extends Tables>(
    name: T,
    client?: number,
    retryCount: number = 0,
    force: boolean = false
  ): Promise<TableTypes[T]> => {
    const safeName = normalizeTableName(String(name));
    if (!safeName) {
      console.warn(`Tabela desconhecida solicitada: ${String(name)}`);
      return [] as TableTypes[T];
    }

    if (isLoading.current[safeName] && !force) {
      if (retryCount >= MAX_RETRIES) {
        // Evita quebrar a UI em cenários de disputa/hidratação lenta.
        console.warn(`Tempo limite excedido ao carregar ${safeName}. Retornando cache/fallback.`);
        const cachedData = tables.current?.[safeName];
        if (cachedData != null) {
          return cachedData as TableTypes[T];
        }
        return [] as TableTypes[T];
      }
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
      return getOnDB(safeName as T, client, retryCount + 1, force);
    }

    isLoading.current[safeName] = true;

    try {
      const cachedData = tables.current?.[safeName];
      const hasCachedData =
        Array.isArray(cachedData)
          ? cachedData.length > 0
          : !!cachedData && (typeof cachedData !== 'object' || Object.keys(cachedData).length > 0);

      if (hasCachedData && !force) {
        isLoading.current[safeName] = false;
        return cachedData as TableTypes[T];
      }

      if (safeName === 'imagens') {
        const images = (await getImagesFromDB()) as TableTypes['imagens'];
        setOnTables(images, 'imagens', force);
        return images;
      }

      const currDB = await openDBConnection();
      let list = null

      if (safeName === 'produtos'){
        list = await handleProductList(safeName, currDB, client)
      } else {
        list = await handleGet(safeName, currDB)
      }

      if (safeName === 'dicionario_produtos') {
        const isArray = Array.isArray(list);
        const isEmptyObject =
          list && typeof list === 'object' && !Array.isArray(list) && Object.keys(list as object).length === 0;

        // Se não existir, estiver vazio ou vier em formato inesperado (ex.: array), reconstrói o dicionário a partir de produtos
        if (!list || isArray || isEmptyObject) {
          const produtos = await handleProductList('produtos', currDB, client);
          const dicionario = (produtos || []).reduce(
            (acc, i) => ({
              ...acc,
              [i.COD_PRODUTO]: i,
            }),
            {} as Record<number, ProductProps>
          );
          list = dicionario as unknown as TableTypes[T];
        }
      }

      currDB.close();
      setOnTables(list, safeName, force);
      return list as TableTypes[T];
    } catch (error) {
      console.error(`Erro ao obter dados de ${safeName}:`, error);
      return [] as TableTypes[T];
    } finally {
      isLoading.current[safeName] = false;
    }
  };

  const setOnDB = async (data: any, name: Tables, force: boolean = false): Promise<void> => {
    if (!name) {
      return Promise.reject(new Error('Chave ausente'));
    };

    if (!isValidData(data) && !force) {
      return Promise.reject(new Error('Dados inválidos'));
    };
    
    if (name === 'produtos') {
      return Promise.reject(new Error('Não é possível salvar dados em produtos'));
    };

    try {
      if (name === 'imagens') {
        return setImagesOnDB(data);
      }

      const currDB = await openDBConnection().catch(err => {
        throw new Error(`Erro ao abrir conexão com banco de dados para salvar dados em ${name}: ${err?.message ?? err}`);
      });

      const transaction = currDB.transaction(toCoded(name), 'readwrite');
      const store = transaction.objectStore(toCoded(name));

      const allKeys = await store.getAllKeys().catch(err => {
        throw new Error(`Erro ao obter chaves do banco de dados para salvar dados em ${name}: ${err?.message ?? err}`);
      });

      if (allKeys.length) {
        await store.delete(allKeys[allKeys.length - 1]).catch(err => {
          throw new Error(`Erro ao deletar dados do banco de dados para salvar dados em ${name}: ${err?.message ?? err}`);
        });
      }

      await store.put({ data, keyPath: toCoded(name) }).catch(err => {
        throw new Error(`Erro ao salvar dados no banco de dados para salvar dados em ${name}: ${err?.message ?? err}`);
      });

      await transaction.done.catch(err => {
        throw new Error(`Erro ao fechar transação do banco de dados para salvar dados em ${name}: ${err?.message ?? err}`);
      });
      
      closeDBConnection(currDB);
    } catch (error) {
      console.error(`Erro ao salvar dados em ${name}:`, error);
      return Promise.reject(error);
    }
  };

  const reloadTables = async () => {
    for (const table of listTables) {
      await getOnDB(table);
    }
  };

  return {
    getOnDB,
    setOnDB,
    deleteDB,
    deleteImagesDB,
    reloadTables,
    closeDBConnection,
    getImagesFromDB,
    setImagesOnDB,
  };
};
